import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/shared/hooks";
import {
  useSubmitContractForSigningMutation,
  useDownloadWorkContractPreviewMutation,
  useReviewJobApplicationMutation,
  useCompleteHiringMutation,
  useDeleteContractMutation,
} from "@/features/hr/contracts/api";
import { extractErrorMessage } from "@/features/hr/contracts/utils";
import { getWorkerConflictKey } from "@/shared/utils";
import { useOrderSigning } from "@/features/signing";
import type { PromptState } from "./useContractModals";

export interface UseContractMutationsReturn {
  submittingContractId: number | null;
  reviewingJobApplicationCandidateId: number | null;
  completingHiringCandidateId: number | null;
  deletingContractId: number | null;
  isSubmitting: boolean;
  documentSigning: ReturnType<typeof useOrderSigning>;
  handleSubmitForSigning: (id: number) => void;
  handleReviewJobApplication: (
    candidateId: number,
    action: "approve" | "reject" | "revision",
    approval_resolution?: "approved" | "supported" | "no_objection" | "rejected",
    note?: string,
  ) => Promise<void>;
  handleCompleteHiring: (orderId: number) => Promise<void>;
  handleDeleteContract: (id: number) => Promise<void>;
}

export function useContractMutations(
  setPrompt: (state: PromptState | null) => void,
): UseContractMutationsReturn {
  const { t } = useTranslation("ContractsPage");
  const { t: tCommon } = useTranslation("Common");
  const { hasRole } = useAuth();

  const [submitContractForSigning] = useSubmitContractForSigningMutation();
  const [downloadPreview] = useDownloadWorkContractPreviewMutation();
  const [reviewJobApplication] = useReviewJobApplicationMutation();
  const [completeHiring] = useCompleteHiringMutation();
  const [deleteContract] = useDeleteContractMutation();

  const [submittingContractId, setSubmittingContractId] = useState<number | null>(null);
  const [reviewingJobApplicationCandidateId, setReviewingJobApplicationCandidateId] = useState<number | null>(null);
  const [completingHiringCandidateId, setCompletingHiringCandidateId] = useState<number | null>(null);
  const [deletingContractId, setDeletingContractId] = useState<number | null>(null);

  const signingTargetIdRef = useRef<number | null>(null);

  const documentSigning = useOrderSigning({
    previewFn() {
      return downloadPreview(signingTargetIdRef.current!).unwrap().then((r) => r.blob);
    },
    signFn(cmsSignature, fileBase64) {
      return submitContractForSigning({
        id: signingTargetIdRef.current!,
        cms_signature: cmsSignature,
        file_base64: fileBase64!,
      }).unwrap();
    },
    onSuccess() {
      setPrompt({
        title: t("messages.submitSuccessTitle"),
        text: t("messages.submitSuccessText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const isSubmitting = submittingContractId !== null || documentSigning.isLoading;

  function handleSubmitForSigning(id: number) {
    if (isSubmitting) return;
    if (!hasRole("chief")) {
      setPrompt({
        title: tCommon("signing.chiefOnly.title"),
        text: tCommon("signing.chiefOnly.text"),
        variant: "error",
      });
      return;
    }
    signingTargetIdRef.current = id;
    setSubmittingContractId(id);
    documentSigning.handleSign().then(() => {
      setSubmittingContractId(null);
    });
  }

  async function handleReviewJobApplication(
    candidateId: number,
    action: "approve" | "reject" | "revision",
    approval_resolution?: "approved" | "supported" | "no_objection" | "rejected",
    note?: string,
  ) {
    if (reviewingJobApplicationCandidateId !== null) return;

    try {
      setReviewingJobApplicationCandidateId(candidateId);
      const body: {
        action: "approve" | "reject" | "revision";
        approval_resolution?: "approved" | "supported" | "no_objection" | "rejected";
        note?: string;
      } = {
        action,
      };
      if (action === "approve" && approval_resolution) {
        body.approval_resolution = approval_resolution;
      }
      if (note) {
        body.note = note;
      }
      await reviewJobApplication({ candidateId, body }).unwrap();

      setPrompt({
        title: t("jobApplication.messages.uploadSuccess"),
        text:
          action === "approve"
            ? t("jobApplication.messages.approveSuccess")
            : action === "revision"
              ? t("jobApplication.messages.revisionSuccess")
              : t("jobApplication.messages.rejectSuccess"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.errorTitle", t),
        variant: "error",
      });
    } finally {
      setReviewingJobApplicationCandidateId(null);
    }
  }

  async function handleCompleteHiring(orderId: number) {
    if (completingHiringCandidateId !== null) return;

    try {
      setCompletingHiringCandidateId(orderId);
      await completeHiring(orderId).unwrap();
      setPrompt({
        title: t("messages.finalizeSuccessTitle"),
        text: t("order.messages.completeSuccess"),
        variant: "success",
      });
    } catch (err: unknown) {
      const conflictKey = getWorkerConflictKey(err);
      setPrompt({
        title: t("messages.errorTitle"),
        text: conflictKey ? tCommon(conflictKey) : extractErrorMessage(err, "order.messages.completeError", t),
        variant: "error",
      });
    } finally {
      setCompletingHiringCandidateId(null);
    }
  }

  async function handleDeleteContract(id: number) {
    if (deletingContractId !== null) return;

    try {
      setDeletingContractId(id);
      await deleteContract(id).unwrap();
      setPrompt({
        title: t("messages.deleteContractSuccessTitle"),
        text: t("messages.deleteContractSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.deleteContractError", t),
        variant: "error",
      });
    } finally {
      setDeletingContractId(null);
    }
  }

  return {
    submittingContractId,
    reviewingJobApplicationCandidateId,
    completingHiringCandidateId,
    deletingContractId,
    isSubmitting,
    documentSigning,
    handleSubmitForSigning,
    handleReviewJobApplication,
    handleCompleteHiring,
    handleDeleteContract,
  };
}
